import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Form, Row, Card } from "antd";
import {
  CheckOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  ProfileOutlined,
} from "@ant-design/icons";

import {
  selectUserEditErrors,
  selectUserFromUserState,
} from "../../../redux-toolkit/user/user-selector";
import ContentTitle from "../../../components/ContentTitle/ContentTitle";
import AccountDataItem from "../../../components/AccountDataItem/AccountDataItem";
import FormInput from "../../../components/FormInput/FormInput";
import IconButton from "../../../components/IconButton/IconButton";
import { updateUserInfo } from "../../../redux-toolkit/user/user-thunks";
import { resetInputForm } from "../../../redux-toolkit/user/user-slice";

interface PersonalData {
  firstName: string;
  lastName: string;
  city: string;
  address: string;
  phoneNumber: string;
  postIndex: string;
}

const PersonalData: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const usersData = useSelector(selectUserFromUserState);
  const errors = useSelector(selectUserEditErrors);
  const [showUserData, setShowUserData] = useState<boolean>(false);
  const { firstNameError, lastNameError } = errors;

  const onClickShowUserData = (): void => {
    setShowUserData((prevState) => !prevState);
  };

  useEffect(() => {
    dispatch(resetInputForm());
    if (usersData) {
      form.setFieldsValue(usersData);
    }
  }, [dispatch, usersData, form]);

  const onFormSubmit = (data: PersonalData): void => {
    dispatch(updateUserInfo({ id: usersData?.id, ...data }));
  };

  return (
    <div
      style={{
        width: "100%",
        padding: "32px 48px",
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
        minHeight: "calc(100vh - 160px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <ContentTitle
          title="My Account"
          titleLevel={4}
          icon={<ProfileOutlined />}
        />
        <Button
          type="primary"
          onClick={onClickShowUserData}
          icon={showUserData ? <EyeInvisibleOutlined /> : <EditOutlined />}
        >
          {showUserData ? "Hide" : "Edit"}
        </Button>
      </div>

      {/* Content */}
      <Row gutter={[32, 32]} style={{ flex: 1, width: "100%" }}>
        {/* Left side: current data */}
        <Col xs={24} md={12} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <AccountDataItem title="Email" text={usersData?.email} />
          <AccountDataItem title="First name" text={usersData?.firstName} />
          <AccountDataItem title="Last name" text={usersData?.lastName} />
          <AccountDataItem title="City" text={usersData?.city} />
          <AccountDataItem title="Address" text={usersData?.address} />
          <AccountDataItem title="Phone number" text={usersData?.phoneNumber} />
          <AccountDataItem title="Post index" text={usersData?.postIndex} />
        </Col>

        {/* Right side: editable form */}
        {showUserData && (
          <Col xs={24} md={12}>
            <Form
              onFinish={onFormSubmit}
              form={form}
              layout="vertical"
              style={{ width: "100%" }}
            >
              <FormInput
                title="First name:"
                titleSpan={6}
                wrapperSpan={18}
                name="firstName"
                error={firstNameError}
                placeholder="First name"
              />
              <FormInput
                title="Last name:"
                titleSpan={6}
                wrapperSpan={18}
                name="lastName"
                error={lastNameError}
                placeholder="Last name"
              />
              <FormInput
                title="City:"
                titleSpan={6}
                wrapperSpan={18}
                name="city"
                placeholder="City"
              />
              <FormInput
                title="Address:"
                titleSpan={6}
                wrapperSpan={18}
                name="address"
                placeholder="Address"
              />
              <FormInput
                title="Phone number:"
                titleSpan={6}
                wrapperSpan={18}
                name="phoneNumber"
                placeholder="Phone number"
              />
              <FormInput
                title="Post index:"
                titleSpan={6}
                wrapperSpan={18}
                name="postIndex"
                placeholder="Post index"
              />
              <div style={{ textAlign: "right", marginTop: 16 }}>
                <IconButton title="Save" icon={<CheckOutlined />} />
              </div>
            </Form>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default PersonalData;
